import React, { useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { isAdminUser } from "../../utils/constants";
import "../../pages/Dashboard.scss";
import icSidenav1 from "../../assets/images/ic_Home.svg";
import icSidenav2 from "../../assets/images/Ic_people.svg";
import icSidenav3 from "../../assets/images/ic_performance.svg";
import icToken from "../../assets/images/ic_tokens.svg";
import icFeedback from "../../assets/images/ic_feedback.svg";
// import icSidenav4 from "../../assets/images/ic_FirstAID.svg";
// import icSidenav5 from "../../assets/images/ic_accessibilty.svg";
import icSidenav6 from "../../assets/images/ic_phone.svg";
import icSidenav7 from "../../assets/images/ic_procurement.svg";
import mainLogo from "../../assets/images/accellorAILabs_logo_s.svg";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";

const CONTACT_POPOVER_CONTENT = (
  <>
    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
      Please contact below for any queries:
    </Typography>
    <Typography variant="body2" component="div" sx={{ lineHeight: 1.8 }}>
      Vaibhav Sharma –{" "}
      <Box
        component="a"
        href="mailto:vaibhav.sharma@accellor.com"
        sx={{ color: "primary.main" }}
      >
        vaibhav.sharma@accellor.com
      </Box>
      <br />
      Ranjan Tatke –{" "}
      <Box
        component="a"
        href="mailto:ranjan.tatke@accellor.com"
        sx={{ color: "primary.main" }}
      >
        ranjan.tatke@accellor.com
      </Box>
    </Typography>
  </>
);

type SidebarProps = {
  activeNav?: string;
  onNavClick?: (navId: string) => void;
  user?: { name?: string; username?: string };
  onLogout?: () => void;
};

export default function Sidebar({
  activeNav = "home",
  onNavClick,
  user,
  onLogout,
}: SidebarProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Get user_id from Redux to check if admin
  const allAgents = useSelector((state: RootState) => state.agents.data);
  const currentUserId = useMemo(() => {
    if (allAgents && allAgents.length > 0) {
      return allAgents[0]?.user_id;
    }
    // Fallback to localStorage
    const storedId = localStorage.getItem('user_id');
    return storedId ? parseInt(storedId, 10) : undefined;
  }, [allAgents]);

  const isAdmin = useMemo(() => isAdminUser(currentUserId), [currentUserId]);

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const popoverOpen = Boolean(popoverAnchorEl);

  const [dashboardSubmenuAnchorEl, setDashboardSubmenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const dashboardSubmenuOpen = Boolean(dashboardSubmenuAnchorEl);
  const dashboardSubmenuLeaveTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const dashboardSubmenuOpenedAtRef = useRef<number>(0);

  const HOVER_OPEN_GUARD_MS = 400;
  const HOVER_CLOSE_DELAY_MS = 200;
  const G42_GREEN = "#08ADC2";

  const handleContactPopoverToggle = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handlePopoverClose = () => setPopoverAnchorEl(null);

  const handleDashboardSubmenuClose = () => setDashboardSubmenuAnchorEl(null);

  const handleDashboardSubmenuMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (dashboardSubmenuLeaveTimerRef.current) {
      clearTimeout(dashboardSubmenuLeaveTimerRef.current);
      dashboardSubmenuLeaveTimerRef.current = null;
    }
    dashboardSubmenuOpenedAtRef.current = Date.now();
    setDashboardSubmenuAnchorEl(event.currentTarget);
  };

  const handleDashboardSubmenuMouseLeave = () => {
    if (Date.now() - dashboardSubmenuOpenedAtRef.current < HOVER_OPEN_GUARD_MS)
      return;
    dashboardSubmenuLeaveTimerRef.current = setTimeout(() => {
      setDashboardSubmenuAnchorEl(null);
      dashboardSubmenuLeaveTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    onLogout?.();
    handleMenuClose();
  };

  const dashboardSubmenuItems = [
    {
      label: "Contract Performance",
      path: "/contract-performance",
      disabled: false,
    },
    {
      label: "Procurement Dashboard",
      path: "/procurement-dashboard",
      disabled: false,
    },
    {
      label: "Procurement Health",
      path: "/procurement-health",
      disabled: true,
    },
    { label: "Supplier Health", path: "/supplier-health", disabled: true },
    {
      label: "Sourcing & PR Efficiency",
      path: "/pr-efficiency",
      disabled: true,
    },
    {
      path: "/chat/procurement-insights",
      disabled: false,
    },
  ];

  const dashboardRoutes = dashboardSubmenuItems
    .filter((item) => !item.disabled)
    .map((item) => item.path);
  const isDashboardPage = dashboardRoutes.includes(location.pathname);

  const effectiveActiveNav = isDashboardPage ? "dashboard" : activeNav;

  const navItems: { id: string; icon: string }[] = [];

  const userInitial =
    user?.name?.trim().charAt(0).toUpperCase() ||
    user?.username?.trim().charAt(0).toUpperCase() ||
    "U";

  return (
    <aside className="sidebar">
      <IconButton
        onClick={() => navigate("/landing")}
        className="sidebar-logo"
        aria-label="Go to landing page"
      >
        <img src={mainLogo} alt="Accellor logo" width={40} height={40} />
      </IconButton>

      <nav className="nav">
        {navItems.map((item) => {
          const isContactPopoverIcon = item.id === "notifications";
          const isDashboard = item.id === "dashboard";
          const showDashboardSubmenu = isDashboard && dashboardSubmenuOpen;
          return (
            <React.Fragment key={item.id}>
              <button
                className={`nav-btn ${effectiveActiveNav === item.id ? "active" : ""}`}
                onClick={(e) => {
                  if (isContactPopoverIcon) {
                    handleContactPopoverToggle(e);
                  } else if (isDashboard) {
                    // optional: keep current page or navigate
                  } else if (onNavClick) {
                    onNavClick(item.id);
                  } else {
                    if (item.id === "home") {
                      navigate("/landing");
                    } else if (item.id === "dashboard") {
                      navigate("/procurement-dashboard");
                    } else if (item.id === "analytics") {
                      navigate("/onboarding");
                    } else if (item.id === "search") {
                      navigate("/performance");
                    } else if (item.id === "token") {
                      navigate("/tokenconsumption");
                    } else if (item.id === "feedback") {
                      navigate("/feedback");
                    }
                  }
                }}
                onMouseEnter={
                  isDashboard
                    ? handleDashboardSubmenuMouseEnter
                    : undefined
                }
                onMouseLeave={
                  isDashboard
                    ? handleDashboardSubmenuMouseLeave
                    : undefined
                }
                aria-label={item.id === "feedback" ? "Feedback" : item.id}
                aria-haspopup={
                  isContactPopoverIcon || isDashboard
                    ? "true"
                    : undefined
                }
                aria-expanded={
                  (isContactPopoverIcon && popoverOpen) || showDashboardSubmenu
                    ? "true"
                    : undefined
                }
              >
                <img src={item.icon} alt={item.id} className="nav-icon" />
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        disableEnforceFocus
        disableRestoreFocus
        sx={{ ml: 1.5 }}
      >
        <Box sx={{ position: "relative", p: 2, minWidth: 360, maxWidth: 360 }}>
          <IconButton
            size="small"
            onClick={handlePopoverClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {CONTACT_POPOVER_CONTENT}
        </Box>
      </Popover>

      {isDashboardPage && (
        <Popover
          open={dashboardSubmenuOpen}
          anchorEl={dashboardSubmenuAnchorEl}
          onClose={handleDashboardSubmenuClose}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          disableEnforceFocus
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: () => {
              if (dashboardSubmenuLeaveTimerRef.current) {
                clearTimeout(dashboardSubmenuLeaveTimerRef.current);
                dashboardSubmenuLeaveTimerRef.current = null;
              }
            },
            onMouseLeave: handleDashboardSubmenuMouseLeave,
          }}
          sx={{ ml: 1.5 }}
        >
          <Box sx={{ py: 0.5, minWidth: 160 }}>
            {dashboardSubmenuItems.map((item) => {
              const isSelected = location.pathname === item.path;
              const isDisabled = item.disabled || false;
              return (
                <MenuItem
                  key={item.path}
                  selected={isSelected && !isDisabled}
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      navigate(item.path);
                      handleDashboardSubmenuClose();
                    }
                  }}
                  sx={{
                    fontSize: 14,
                    py: 1.25,
                    px: 2,
                    ...(isDisabled && {
                      color: "#9e9e9e",
                      cursor: "not-allowed",
                      opacity: 0.6,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }),
                    ...(isSelected &&
                      !isDisabled && {
                        color: G42_GREEN,
                        fontWeight: 600,
                        backgroundColor: "rgba(8, 173, 194, 0.08)",
                      }),
                  }}
                >
                  <ListItemText primary={item.label} />
                </MenuItem>
              );
            })}
          </Box>
        </Popover>
      )}

      <div className="sidebar-profile">
        {isAdmin && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Tooltip title="User Management" placement="right" arrow>
              <IconButton
                onClick={() => navigate("/user-management")}
                className="sidebar-admin-btn"
                aria-label="Admin Panel"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor:
                    location.pathname === "/user-management"
                      ? "rgba(8, 173, 194, 0.1)"
                      : "transparent",
                  color:
                    location.pathname === "/user-management"
                      ? "#08ADC2"
                      : "#666",
                  "&:hover": {
                    backgroundColor: "rgba(8, 173, 194, 0.1)",
                    color: "#08ADC2",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <AdminPanelSettingsIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                color:
                  location.pathname === "/user-management"
                    ? "#08ADC2"
                    : "#666",
                mt: 0.5,
                fontWeight:
                  location.pathname === "/user-management" ? 600 : 400,
              }}
            >
              Admin
            </Typography>
          </Box>
        )}
        {isAdmin && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Tooltip title="Agent Management" placement="right" arrow>
              <IconButton
                onClick={() => navigate("/agent-management")}
                className="sidebar-admin-btn"
                aria-label="Agent Management"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor:
                    location.pathname === "/agent-management"
                      ? "rgba(8, 173, 194, 0.1)"
                      : "transparent",
                  color:
                    location.pathname === "/agent-management"
                      ? "#08ADC2"
                      : "#666",
                  "&:hover": {
                    backgroundColor: "rgba(8, 173, 194, 0.1)",
                    color: "#08ADC2",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <SmartToyOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                color:
                  location.pathname === "/agent-management"
                    ? "#08ADC2"
                    : "#666",
                mt: 0.5,
                fontWeight:
                  location.pathname === "/agent-management" ? 600 : 400,
              }}
            >
              Agents
            </Typography>

            <Tooltip title="Categories & Industries" placement="right" arrow>
              <IconButton
                onClick={() => navigate("/agent-lookup-management")}
                className="sidebar-admin-btn"
                aria-label="Categories & Industries"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  mt: 1,
                  backgroundColor:
                    location.pathname === "/agent-lookup-management"
                      ? "rgba(8, 173, 194, 0.1)"
                      : "transparent",
                  color:
                    location.pathname === "/agent-lookup-management"
                      ? "#08ADC2"
                      : "#666",
                  "&:hover": {
                    backgroundColor: "rgba(8, 173, 194, 0.1)",
                    color: "#08ADC2",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CategoryOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                color:
                  location.pathname === "/agent-lookup-management"
                    ? "#08ADC2"
                    : "#666",
                mt: 0.5,
                fontWeight:
                  location.pathname === "/agent-lookup-management" ? 600 : 400,
              }}
            >
              Masters
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={handleMenuOpen}
          className="sidebar-profile-btn"
          aria-controls={open ? "sidebar-user-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <div
            className="avatar avatar-initial"
            style={{
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            }}
          >
            {userInitial}
          </div>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    </aside>
  );
}
